import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import type { ComboboxHandle } from '@lumx/core/js/components/Combobox/types';
import { useId } from '@lumx/react/hooks/useId';

import { ComboboxContext } from './context/ComboboxContext';
import { ListboxContext, ListboxContextValue } from '../listbox/context/ListboxContext';

/**
 * Defines the props of the component.
 */
export interface ComboboxProviderProps {
    /** Combobox content. */
    children?: ReactNode;
    /** Callback fired when the combobox open state changes. */
    onOpen?: (isOpen: boolean) => void;
    /**
     * Keep the listbox always open (inline listbox).
     * By default, the listbox is always open when the combobox contains no `Combobox.Popover`.
     */
    alwaysOpen?: boolean;
}

/**
 * Combobox.Provider component.
 *
 * Provides shared context to sub-components. The vanilla JS combobox handle is
 * created by the trigger sub-component (Combobox.Input or Combobox.Button) on mount.
 *
 * @param props Component props.
 * @return React element.
 */
export function ComboboxProvider(props: ComboboxProviderProps) {
    const { children, onOpen, alwaysOpen } = props;
    const listboxId = useId();
    const anchorRef = useRef<HTMLElement>(null);
    const [handle, setHandle] = useState<ComboboxHandle | null>(null);
    const contextValue = useMemo(() => ({ handle, setHandle, listboxId, anchorRef }), [handle, listboxId]);
    // The listbox of the combobox: shared with the list, the options and the state through the listbox context.
    const listboxContextValue = useMemo<ListboxContextValue>(
        () => ({ list: handle?.list ?? null, listboxId, type: 'listbox' }),
        [handle, listboxId],
    );

    // Force the always open state (`undefined`: the core detects the inline listbox).
    useEffect(() => {
        handle?.setAlwaysOpen(alwaysOpen);
    }, [handle, alwaysOpen]);

    // Subscribe to the open event and forward to the onOpen callback.
    const onOpenRef = useRef(onOpen);
    onOpenRef.current = onOpen;
    useEffect(() => {
        if (!handle) return undefined;
        return handle.list.subscribe('open', (isOpen) => {
            onOpenRef.current?.(isOpen);
        });
    }, [handle]);

    return (
        <ComboboxContext.Provider value={contextValue}>
            <ListboxContext.Provider value={listboxContextValue}>{children}</ListboxContext.Provider>
        </ComboboxContext.Provider>
    );
}

ComboboxProvider.displayName = 'Combobox.Provider';
